import { UnseenReviewBar } from "@/components/admin/UnseenReviewBar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AdminShell } from "@/components/admin/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Loader2, RefreshCw, Plus, Download, Undo2, Scale, ExternalLink, FileWarning, DownloadCloud,
  Lock, History, Landmark,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AddTransactionDialog } from "@/components/admin/ledger/AddTransactionDialog";
import { BackfillDialog } from "@/components/admin/ledger/BackfillDialog";
import { ReconcileDialog } from "@/components/admin/ledger/ReconcileDialog";
import { LedgerReports } from "@/components/admin/ledger/LedgerReports";
import { CardDrilldownDialog } from "@/components/admin/ledger/CardDrilldownDialog";
import { FinancialHealthCard } from "@/components/admin/ledger/FinancialHealthCard";
import { CashFlowStatement } from "@/components/admin/ledger/CashFlowStatement";

import { CloseMonthDialog } from "@/components/admin/ledger/CloseMonthDialog";
import { FinancialAuditLogDialog } from "@/components/admin/ledger/FinancialAuditLogDialog";
import { BankReconciliationDialog } from "@/components/admin/ledger/BankReconciliationDialog";
import {
  getLedgerSummary, listLedgerEntries, captureDailySnapshot, listPeriodClosures, isPeriodClosed, listLedgerCategories, reverseTransaction,
  adjustTransaction, attachReceipt, money, formatMethod, formatStatus, sourceLink, toCsv,
  PERIOD_OPTIONS, PAYMENT_METHODS, FINANCIAL_START_DATE,
  type LedgerEntry, type LedgerSummaryResponse,
} from "@/lib/financialLedger";

function formatPayDate(iso: string): string {
  if (!iso || iso === "unscheduled") return "an unscheduled date";
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
}


export default function FinancialLedgerPage() {
  const { toast } = useToast();
  const [period, setPeriod] = useState<string>("this_month");
  const [customStart, setCustomStart] = useState(FINANCIAL_START_DATE);
  const [customEnd, setCustomEnd] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState<LedgerSummaryResponse | null>(null);
  const [rows, setRows] = useState<LedgerEntry[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [direction, setDirection] = useState("all");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [method, setMethod] = useState("all");
  const [origin, setOrigin] = useState("all");
  const [reconciled, setReconciled] = useState("all");

  const [addOpen, setAddOpen] = useState(false);
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [drilldown, setDrilldown] = useState<string | null>(null);
  const [explain, setExplain] = useState<"payroll" | "net" | null>(null);

  const [closeMonthOpen, setCloseMonthOpen] = useState(false);
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [bankOpen, setBankOpen] = useState(false);
  const [closures, setClosures] = useState<any[]>([]);
  const [healthKey, setHealthKey] = useState(0);
  const [actionEntry, setActionEntry] = useState<{ entry: LedgerEntry; mode: "reverse" | "adjust" | "receipt" } | null>(null);
  const [actionReason, setActionReason] = useState("");
  const [actionAmount, setActionAmount] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  const [instructors, setInstructors] = useState<Array<{ id: string; name: string }>>([]);
  const [classList, setClassList] = useState<Array<{ slug: string; title: string }>>([]);

  const range = period === "custom" ? { start_date: customStart, end_date: customEnd } : undefined;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, l] = await Promise.all([
        getLedgerSummary(period, range),
        listLedgerEntries(period, { direction, category, status, payment_method: method, origin, reconciled }, range),
      ]);
      setSummary(s);
      setRows(l.rows);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, customStart, customEnd, direction, category, status, method, origin, reconciled]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const c = await listLedgerCategories();
        setCategories(c.categories);
      } catch { /* non-fatal */ }
      const { data: ins } = await supabase.from("instructors").select("id, full_name, preferred_name").order("full_name");
      setInstructors((ins ?? []).map((i: any) => ({ id: i.id, name: i.preferred_name || i.full_name })));
      // class_registry has no `title` column — titles live on `classes`.
      const { data: cls } = await supabase.from("classes").select("slug, title").order("title");
      setClassList((cls ?? []).filter((c: any) => c.slug).map((c: any) => ({ slug: c.slug, title: c.title })));
      try {
        const cl = await listPeriodClosures();
        setClosures(cl.closures);
      } catch { /* non-fatal */ }
      // Capture today's financial snapshot once per day so historical trends
      // never need to be recalculated from past state.
      try {
        const stamp = new Date().toISOString().slice(0, 10);
        if (localStorage.getItem("ss-financial-snapshot") !== stamp) {
          await captureDailySnapshot();
          localStorage.setItem("ss-financial-snapshot", stamp);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  const s = summary?.summary;
  const sc = summary?.source_counts;
  const p = summary?.period;
  const forecast = summary?.payroll_forecast;
  const projectionLines = summary?.projected_net_breakdown ?? [];


  const exportCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `silver-sage-ledger-${p?.start_date ?? "all"}-to-${p?.end_date ?? "now"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runAction = async () => {
    if (!actionEntry) return;
    setActionBusy(true);
    try {
      if (actionEntry.mode === "reverse") {
        await reverseTransaction(actionEntry.entry.id, actionReason);
        toast({ title: "Transaction reversed", description: "The original record is preserved with a linked reversal." });
      } else if (actionEntry.mode === "adjust") {
        const cents = Math.round(Number(actionAmount.replace(/[^0-9.\-]/g, "")) * 100);
        await adjustTransaction(actionEntry.entry.id, cents, actionReason);
        toast({ title: "Adjustment recorded" });
      } else {
        await attachReceipt(actionEntry.entry.id, actionAmount);
        toast({ title: "Receipt attached" });
      }
      setActionEntry(null); setActionReason(""); setActionAmount("");
      afterFinancialChange();
    } catch (e) {
      toast({ title: "Could not complete", description: (e as Error).message, variant: "destructive" });
    } finally {
      setActionBusy(false);
    }
  };

  /** Any financial event re-runs reconciliation and refreshes health. */
  const afterFinancialChange = useCallback(async () => {
    await load();
    try {
      const cl = await listPeriodClosures();
      setClosures(cl.closures);
    } catch { /* non-fatal */ }
    setHealthKey((k) => k + 1);
  }, [load]);

  const categoryOptions = useMemo(
    () => [...new Set([...categories, "Registration Revenue", "Instructor Payroll", "Refund", "Supply Purchase"])].sort(),
    [categories],
  );

  return (
    <AdminShell section="reports">
      <div className="container mx-auto p-4 md:p-6 space-y-5">
        <UnseenReviewBar
          badge="ledger"
          note="Marking these read only clears the sidebar bubble — no ledger entry is altered or reconciled."
        />

        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground">Financial Ledger</h1>
            <p className="text-sm text-muted-foreground max-w-2xl">
              Every dollar in and out of Silver Sage since {FINANCIAL_START_DATE}. Registration payments, refunds,
              payroll, and Grace payments post here automatically from their own records — nothing is entered twice.
            </p>
            {summary?.reconciled_through && (
              <p className="text-xs text-muted-foreground mt-1">
                Reconciled through {summary.reconciled_through}.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1" /> Add Transaction</Button>
            <Button size="sm" variant="outline" onClick={() => setBackfillOpen(true)}><DownloadCloud className="h-4 w-4 mr-1" /> Import existing</Button>
            <Button size="sm" variant="outline" onClick={() => setReconcileOpen(true)}><Scale className="h-4 w-4 mr-1" /> Reconcile</Button>
            <Button size="sm" variant="outline" onClick={() => setBankOpen(true)}><Landmark className="h-4 w-4 mr-1" /> Bank Reconciliation</Button>
            <Button size="sm" variant="outline" onClick={() => setCloseMonthOpen(true)}><Lock className="h-4 w-4 mr-1" /> Close Month</Button>
            <Button size="sm" variant="outline" onClick={() => setAuditLogOpen(true)}><History className="h-4 w-4 mr-1" /> Audit Log</Button>
            <Button size="sm" variant="outline" onClick={exportCsv} disabled={!rows.length}><Download className="h-4 w-4 mr-1" /> Export</Button>
            <Button size="sm" variant="ghost" onClick={load} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>

        {/* Period */}
        <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 lg:flex lg:flex-wrap">
          <div className="space-y-1 min-w-0">
            <Label className="text-xs">Period</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-full sm:w-[220px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {period === "custom" && (
            <>
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="w-full sm:w-[160px]" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="w-full sm:w-[160px]" />
              </div>
            </>
          )}
          {p && <p className="text-xs text-muted-foreground pb-2">Showing {p.label}: {formatPayDate(p.start_date)} through {formatPayDate(p.end_date)}</p>}
        </div>

        <FinancialHealthCard key={healthKey} />

        {p && isPeriodClosed(closures as any, p.end_date) && (
          <Card className="border-primary/40">
            <CardContent className="p-3 text-sm text-muted-foreground flex items-center gap-2">
              <Lock className="h-4 w-4 text-primary" />
              This period has been closed. Its totals are frozen — corrections must be recorded as adjustment or
              reversal transactions.
            </CardContent>
          </Card>
        )}

        {error && (
          <Card className="border-destructive"><CardContent className="p-4 text-sm text-destructive">{error}</CardContent></Card>
        )}

        {/* Summary */}
        {summary && s && (
          <>
            <CashFlowStatement
              summary={summary}
              onDrill={(card) => setDrilldown(card)}
              onExplainPayroll={() => setExplain("payroll")}
              onExplainProjection={() => setExplain("net")}
            />

            {s.missing_documentation_count > 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <FileWarning className="h-3.5 w-3.5" />
                {s.missing_documentation_count} transaction{s.missing_documentation_count === 1 ? "" : "s"} missing documentation.
              </p>
            )}
          </>
        )}


        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Transactions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap">
              <Select value={direction} onValueChange={setDirection}>
                <SelectTrigger className="w-full lg:w-[140px]"><SelectValue placeholder="Direction" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All directions</SelectItem>
                  <SelectItem value="in">Money In</SelectItem>
                  <SelectItem value="out">Money Out</SelectItem>
                  <SelectItem value="transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="w-full lg:w-[200px]"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-full lg:w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {["pending", "completed", "paid", "refunded", "partially_refunded", "voided", "reversed", "failed", "projected"].map((v) => (
                    <SelectItem key={v} value={v}>{formatStatus(v as any)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger className="w-full lg:w-[150px]"><SelectValue placeholder="Method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All methods</SelectItem>
                  {PAYMENT_METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={origin} onValueChange={setOrigin}>
                <SelectTrigger className="w-full lg:w-[150px]"><SelectValue placeholder="Origin" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Automatic & manual</SelectItem>
                  <SelectItem value="automatic">Automatic</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={reconciled} onValueChange={setReconciled}>
                <SelectTrigger className="w-full lg:w-[160px]"><SelectValue placeholder="Reconciled" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any reconciliation</SelectItem>
                  <SelectItem value="reconciled">Reconciled</SelectItem>
                  <SelectItem value="unreconciled">Unreconciled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading transactions…
              </div>
            ) : rows.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8">
                No transactions in this period yet. Use <span className="font-medium">Import existing</span> to bring in
                payments and payroll already on record.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Transaction ID</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Money In</TableHead>
                      <TableHead className="text-right">Money Out</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Linked</TableHead>
                      <TableHead>Entered By</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const link = sourceLink(r);
                      return (
                        <TableRow key={r.id} className={r.voided_at ? "opacity-60" : ""}>
                          <TableCell className="whitespace-nowrap text-xs">{r.transaction_date}</TableCell>
                          <TableCell className="whitespace-nowrap font-mono text-[11px]">{r.transaction_ref ?? "—"}</TableCell>
                          <TableCell className="text-xs">
                            {r.direction === "in" ? "Money In" : r.direction === "out" ? "Money Out" : "Transfer"}
                          </TableCell>
                          <TableCell className="text-xs max-w-[260px]">
                            <span className="block whitespace-normal break-words" title={r.description}>{r.description}</span>
                            {r.student_name && <span className="text-[11px] text-muted-foreground">Student: {r.student_name}</span>}
                          </TableCell>
                          <TableCell className="text-xs">{r.category}</TableCell>
                          <TableCell className="text-right text-xs">{r.direction === "in" ? money(r.amount_cents) : ""}</TableCell>
                          <TableCell className="text-right text-xs">{r.direction === "out" ? money(r.amount_cents) : ""}</TableCell>
                          <TableCell className="text-xs">{formatMethod(r.payment_method)}</TableCell>
                          <TableCell>
                            <Badge variant={r.voided_at ? "outline" : "secondary"} className="text-[10px]">
                              {r.voided_at ? "Voided" : formatStatus(r.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {link ? (
                              <Link to={link.href} className="inline-flex items-center gap-1 text-primary hover:underline">
                                {link.label} <ExternalLink className="h-3 w-3" />
                              </Link>
                            ) : "—"}
                          </TableCell>
                          <TableCell className="text-xs">{r.created_by_label ?? "—"}</TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {!r.voided_at && (
                              <>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                                  onClick={() => { setActionEntry({ entry: r, mode: "reverse" }); setActionReason(""); }}>
                                  <Undo2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                                  onClick={() => { setActionEntry({ entry: r, mode: "adjust" }); setActionReason(""); setActionAmount(""); }}>
                                  Adjust
                                </Button>
                                {r.documentation_state === "missing" && (
                                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                                    onClick={() => { setActionEntry({ entry: r, mode: "receipt" }); setActionAmount(""); }}>
                                    Receipt
                                  </Button>
                                )}
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <LedgerReports period={period} range={range} />
      </div>

      <AddTransactionDialog open={addOpen} onOpenChange={setAddOpen} onSaved={afterFinancialChange} instructors={instructors} classes={classList} />
      <BackfillDialog open={backfillOpen} onOpenChange={setBackfillOpen} onImported={afterFinancialChange} />
      <CloseMonthDialog open={closeMonthOpen} onOpenChange={setCloseMonthOpen} onDone={afterFinancialChange} />
      <FinancialAuditLogDialog open={auditLogOpen} onOpenChange={setAuditLogOpen} />
      <BankReconciliationDialog open={bankOpen} onOpenChange={setBankOpen} onChanged={afterFinancialChange} />
      <ReconcileDialog
        open={reconcileOpen}
        onOpenChange={setReconcileOpen}
        startDate={p?.start_date ?? FINANCIAL_START_DATE}
        endDate={p?.end_date ?? new Date().toISOString().slice(0, 10)}
        onDone={afterFinancialChange}
      />

      <Dialog open={!!actionEntry} onOpenChange={(v) => !v && setActionEntry(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionEntry?.mode === "reverse" ? "Reverse transaction"
                : actionEntry?.mode === "adjust" ? "Add adjustment" : "Attach receipt"}
            </DialogTitle>
            <DialogDescription>
              {actionEntry?.mode === "receipt"
                ? "Link a receipt or invoice to this transaction."
                : "The original transaction is preserved. A linked entry records the correction with a full audit trail."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {actionEntry?.entry.description} · {money(actionEntry?.entry.amount_cents ?? 0)}
            </p>
            {actionEntry?.mode === "adjust" && (
              <div className="space-y-1.5">
                <Label htmlFor="adj-amount">Adjustment amount (use a negative number to reduce)</Label>
                <Input id="adj-amount" value={actionAmount} onChange={(e) => setActionAmount(e.target.value)} placeholder="0.00" />
              </div>
            )}
            {actionEntry?.mode === "receipt" ? (
              <div className="space-y-1.5">
                <Label htmlFor="receipt-url">Receipt link</Label>
                <Input id="receipt-url" value={actionAmount} onChange={(e) => setActionAmount(e.target.value)} placeholder="https://…" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="action-reason">Reason (required)</Label>
                <Textarea id="action-reason" value={actionReason} onChange={(e) => setActionReason(e.target.value)} rows={3} />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setActionEntry(null)}>Cancel</Button>
            <Button
              onClick={runAction}
              disabled={actionBusy || (actionEntry?.mode !== "receipt" && !actionReason.trim())}
            >
              {actionBusy ? "Working…" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CardDrilldownDialog card={drilldown} period={period} range={range} onClose={() => setDrilldown(null)} />

      <Dialog open={explain !== null} onOpenChange={(o) => !o && setExplain(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {explain === "payroll" ? "Remaining payroll this month" : "Projected Month-End Net"}
            </DialogTitle>
            <DialogDescription>
              {explain === "payroll"
                ? forecast?.basis ?? "Canonical payroll rules — meetings already paid are excluded."
                : "What the Hub is projected to retain once every expected dollar in and every instructor payment due this month is accounted for."}
            </DialogDescription>
          </DialogHeader>

          {explain === "payroll" && (
            <div className="space-y-4">
              {(!forecast || forecast.pay_dates.length === 0) && (
                <p className="text-sm text-muted-foreground">
                  No instructor payments remain for this period. Everything scheduled has already been paid.
                </p>
              )}
              {forecast?.pay_dates.map((d) => (
                <div key={d.pay_date} className="rounded-lg border p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Pay date — {formatPayDate(d.pay_date)}</p>
                    <p className="text-sm font-heading font-bold">{money(d.amount_cents)}</p>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Instructor</TableHead>
                        <TableHead>Meetings</TableHead>
                        <TableHead>How they're paid</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {d.instructors.map((i, idx) => (
                        <TableRow key={`${d.pay_date}-${i.instructor_name}-${idx}`}>
                          <TableCell>{i.instructor_name}</TableCell>
                          <TableCell>{i.meeting_count ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {i.schedule_label ?? formatMethod(i.payment_method ?? "") ?? "—"}
                          </TableCell>
                          <TableCell className="text-right">{money(i.amount_cents)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
              {forecast && forecast.pay_dates.length > 0 && (
                <div className="flex items-center justify-between border-t pt-3">
                  <p className="text-sm font-medium">Total still to pay</p>
                  <p className="text-lg font-heading font-bold">{money(forecast.remaining_cents)}</p>
                </div>
              )}
              {forecast?.needs_review?.length ? (
                <p className="text-xs text-muted-foreground">
                  {forecast.needs_review.length} item{forecast.needs_review.length === 1 ? "" : "s"} need a second look
                  before payday — open Payroll to resolve the pay rate.
                </p>
              ) : null}
            </div>
          )}

          {explain === "net" && s && (
            <div className="space-y-2">
              {projectionLines.map((line, i) => (
                <div key={`${line.label}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{line.label}</span>
                  <span className={line.sign === "-" ? "text-destructive" : "text-foreground"}>
                    {line.sign === "-" ? "−" : "+"}{money(line.amount_cents)}
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t pt-3">
                <span className="text-sm font-medium">Projected Month-End Net</span>
                <span className="text-lg font-heading font-bold">{money(s.projected_net_cents)}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                Projected figures are never recorded as cash. They update automatically as payments are collected and
                payroll is marked paid.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setExplain(null)}>Close</Button>
            {explain === "payroll" && (
              <Button asChild><Link to="/admin/payroll">Open Payroll</Link></Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </AdminShell>
  );
}
